<template>
  <v-container class="pa-0">
    <Loader v-if="loading" text="" color="black" class="mt-6" />
    <DetailsListCluster
      v-if="items.length > 0"
      theme="light"
      :items="items"
      :page="page"
      :pageSize="pageSize"
      :height-percent="95"
      :disable-scroll="true"
      :show-primary-topics="true"
      :show-thumbs-down="true"
      :get-threshold-type-function="getThresholdTypeFunction"
      :get-live-search-params-function="getParams"
      :scroll-to-top="scrollToTop"
      :show-collaboration="false"
      :matomo-category="analyticsCategory"
      @get-data="getContent"
      @loaded="loading = false"
    />
    <h4 class="text-center mt-4" v-if="!loading && items.length === 0">
      No evacuations or risk rating changes available.
    </h4>
  </v-container>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import lodashGet from "lodash/get";
import lodashIsEqual from "lodash/isEqual";
import lodashDebounce from "lodash/debounce";
import lodashCloneDeep from "lodash/cloneDeep";
import { getArticles } from "@/services/search/wod.service";
import { setInfiniteScrollState } from "@/common/basic.utilities";
import { EVENT_CATEGORIES } from "@/common/constants/analytics";

export default {
  name: "RiskRatingChanges",
  data: () => ({
    loading: false,
    period: 0,
    regions: [],
    regionFilters: [],
    countryFilters: [],
    items: [],
    page: 1,
    pageSize: 25,
    timeout: null,
    scrollToTop: new Date().toISOString(),
    lastParams: null,
    paramsLastChanged: null,
  }),
  components: {
    DetailsListCluster: () =>
      import("@/components/common/details/DetailsListCluster"),
    Loader: () => import("@/components/common/Loader"),
  },
  props: {
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
    analyticsCategory: {
      type: String,
      default: EVENT_CATEGORIES.control_risk_risk_rating_changes,
    },
  },
  methods: {
    async initialize() {
      this.loading = true;

      this.getContent();
    },
    getContent(page, $state, params) {
      if (!page) {
        this.scrollToTop = new Date().toISOString();
      }
      this.loading = true;
      this.getContentDebounced(page, $state, params);
    },
    getContentDebounced: lodashDebounce(async function (page, $state, params) {
      let result;
      let dataParams;
      let paramsLastChanged = this.paramsLastChanged;
      try {
        this.loading = true;

        dataParams = this.getParams(params) || {};

        page = !page ? 1 : page;
        const PAGE_OFFSET = (page - 1) * this.pageSize;

        dataParams.pageSize = this.pageSize;
        dataParams.pageOffset = PAGE_OFFSET;

        let currParams = lodashCloneDeep(dataParams);
        if (currParams.hasOwnProperty("pageOffset")) {
          delete currParams.pageOffset;
        }

        let prevParams = lodashCloneDeep(this.lastParams);
        if (prevParams && prevParams.hasOwnProperty("pageOffset")) {
          delete prevParams.pageOffset;
        }
        if (!lodashIsEqual(currParams, prevParams)) {
          this.requestTimestamp = new Date().toISOString();
        }

        this.lastParams = dataParams;

        result = await getArticles(dataParams, this.lastTimestamp);

        //Only update the component data if there hasn't been a change in search params since we sent the query
        if (paramsLastChanged === this.paramsLastChanged) {
          if (result && result.features) {
            if (
              this.itemsResults &&
              Array.isArray(this.itemsResults) &&
              this.itemsResults.length &&
              page !== 1
            ) {
              this.itemsResults = this.itemsResults.concat(result.features);
            } else {
              this.itemsResults = result.features;
            }
            this.items = lodashCloneDeep(this.itemsResults);

            this.lastTimestamp = lodashGet(
              result,
              "features[0].properties.formatted.requestTimestamp"
            );
          } else {
            this.itemsResults = null;
            this.featuresResults = null;
          }
        }
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
        console.error(error);
      } finally {
        if (paramsLastChanged === this.paramsLastChanged) {
          if (result) {
            setInfiniteScrollState(
              $state,
              { features: result.features },
              "features"
            );
          }
          this.page = page;
          this.loading = false;
        }
        result = null;
        dataParams = null;
        paramsLastChanged = null;
      }
    }, 100),
    getParams() {
      let paramsCopy = lodashCloneDeep(this.params);
      paramsCopy.pageSize = this.pageSize;
      paramsCopy.pageOffset = this.page * this.pageOffset;

      paramsCopy.start = new dayjs()
        .add(-10, "years")
        .startOf("day")
        .toISOString();
      paramsCopy.sources = "riskRatingChanges,evacuations";
      paramsCopy.source = "riskRatingChanges,evacuations";
      paramsCopy.eventType = "verified";
      paramsCopy.aoiId = paramsCopy.regionFilters.concat(
        paramsCopy.countryFilters
      );

      //Delete properties unneeded for WOD request
      delete paramsCopy.bounds;
      delete paramsCopy.regionFilters;
      delete paramsCopy.countryFilters;
      delete paramsCopy.allRegionsSelected;

      return paramsCopy;
    },
    // copied from SearchContent
    getThresholdTypeFunction(doc) {
      let thresholdType = "cluster";
      if (
        lodashGet(doc, "properties.formatted.source") === "twitter.com" ||
        lodashGet(doc, "properties.formatted.source") === "twitter"
      ) {
        thresholdType = "twitter";
      }
      return thresholdType;
    },
    resetState() {
      this.lastTimestamp = null;
      this.lastTimestampDetails = null;

      this.page = 0;
      this.items = [];
    },
  },
  mounted() {
    this.initialize();
  },
  watch: {
    params: {
      async handler() {
        //Save the timestamp of this specific params
        this.paramsLastChanged = new Date().toISOString();

        this.resetState();
        this.getContent();
      },
      deep: true,
    },
  },
};
</script>

<style scoped></style>
